import React, { useState, useEffect } from 'react';
import PortableText from 'react-portable-text';
import { motion } from 'framer-motion';
import AnchorScroll from 'react-anchor-link-smooth-scroll';

import { Arrow } from '@svg/arrow.js';

// Block Components
import {
  AnchorScrollLink,
  ExternalLink,
  PageAnchorLink,
  PageLink,
  PathLink,
} from '../ui/links';
import { ImageModule, VideoModule } from '../modules';
import { EventWidget, FormWidget, Player } from '@modules/widgets';
import { DonateModal } from '@nav/donateModal';
import FoundationDonations from '../foundationDonations';

const LinkType = ({ props, type, setPlayerOpen, toggleDonate, arrowVars }) => {
  switch (type) {
    case 'arrowPlayerLink':
      return (
        <button
          className='font-semibold mt-0-important text-sm leading-sm md:text-lg md:leading-lg w-full flex flex-row justify-between items-center cursor-pointer'
          onClick={() => setPlayerOpen(true)}>
          {props.text}

          <motion.span variants={arrowVars}>
            <Arrow white={props.whiteArrow ? true : false} />
          </motion.span>
        </button>
      );

    case 'arrowPathLink':
      return (
        <PathLink
          className='font-semibold mt-0-important w-full flex flex-row justify-between items-center cursor-pointer'
          {...props.page}>
          <motion.span variants={arrowVars}>
            <Arrow white={props.whiteArrow ? true : false} />
          </motion.span>
        </PathLink>
      );

    case 'arrowUrlLink':
      return (
        <a
          className='font-semibold mt-0-important w-full flex flex-row justify-between items-center cursor-pointer'
          href={props.url}
          target='_blank'
          rel='noreferrer'>
          {props.text}
          <motion.span variants={arrowVars}>
            <Arrow white={props.whiteArrow ? true : false} />
          </motion.span>
        </a>
      );

    case 'arrowLink':
      return (
        <PageLink
          className='font-semibold mt-0-important w-full flex flex-row justify-between items-center cursor-pointer'
          {...props.page}>
          <motion.span variants={arrowVars}>
            <Arrow white={props.whiteArrow ? true : false} />
          </motion.span>
        </PageLink>
      );

    case 'arrowAnchorLink':
      return (
        <AnchorScroll
          href={`#${props?.anchor?.anchor?.current}`}
          className='font-semibold mt-0-important w-full flex flex-row justify-between items-center cursor-pointer'
          {...props}>
          {props?.anchor?.linkText}

          <motion.span variants={arrowVars}>
            <Arrow white={props.whiteArrow ? true : false} />
          </motion.span>
        </AnchorScroll>
      );

    default:
      return (
        <button
          className='font-semibold mt-0-important text-sm leading-sm md:text-lg md:leading-lg w-full flex flex-row justify-between items-center cursor-pointer'
          onClick={toggleDonate}>
          {props.text}

          <motion.span variants={arrowVars}>
            <Arrow white={props.whiteArrow ? true : false} />
          </motion.span>
        </button>
      );
  }
};

const ArrowLinks = ({
  props,
  arrowVars,
  type,
  playerOpen,
  setPlayerOpen,
  toggleDonate,
  donateOpen,
}) => {
  return (
    <motion.div
      initial='initial'
      whileHover='animate'
      className='w-full flex flex-row justify-between items-center cursor-pointer'>
      <LinkType
        props={props}
        type={type}
        setPlayerOpen={setPlayerOpen}
        toggleDonate={toggleDonate}
        arrowVars={arrowVars}
      />

      <Player
        url={props.url}
        playerOpen={playerOpen}
        setPlayerOpen={setPlayerOpen}
      />
      <DonateModal donateOpen={donateOpen} toggleDonate={toggleDonate} />
    </motion.div>
  );
};

export const PortableTextBlock = ({ text, white, setPopupOpen, ...props }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    window.innerWidth < 1024 && setIsMobile(true);
  }, []);

  let arrowVars = {};

  if (!isMobile) {
    arrowVars = {
      initial: {
        x: -20,
        transition: {
          duration: 0.3,
        },
      },
      animate: {
        x: 0,
        transition: {
          duration: 0.3,
        },
      },
    };
  }

  const [playerOpen, setPlayerOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  const toggleDonate = () => setDonateOpen((prev) => !prev);

  const serializers = {
    h0: (props) => <h1 className='title-block' {...props} />,
    h1: (props) => <h1 {...props} />,
    h2: (props) => <h2 {...props} />,
    h3: (props) => <h3 {...props} />,
    h4: (props) => <h4 {...props} />,
    h5: (props) => <h5 {...props} />,
    h6: (props) => <h6 {...props} />,
    'super-script': (props) => <sup>{props.children}</sup>,
    'sub-script': (props) => <sub>{props.children}</sub>,
    pageLink: (props) => (
      <PageLink {...props} className='underline-important' />
    ),
    pathLink: (props) => (
      <PathLink {...props} className='underline-important' />
    ),
    anchorLink: (props) => (
      <AnchorScrollLink
        {...props}
        className='underline-important block sm-18'
      />
    ),
    externalLink: (props) => <ExternalLink {...props} className='underline' />,
    pageAnchorLink: (props) => (
      <PageAnchorLink {...props} className='underline-important sm-18' />
    ),
    normal: (props) => <p {...props} />,
    imageModule: (props) => <ImageModule {...props} />,
    videoModule: (props) => <VideoModule {...props} />,
    roobertMedium: (props) => (
      <span
        className={props.font === 'medium' ? 'font-medium' : 'font-semibold'}>
        {props.children}
      </span>
    ),

    arrowLink: (props) => (
      <ArrowLinks props={props} arrowVars={arrowVars} type='arrowLink' />
    ),
    arrowAnchorLink: (props) => (
      <ArrowLinks props={props} arrowVars={arrowVars} type='arrowAnchorLink' />
    ),
    arrowUrlLink: (props) => (
      <ArrowLinks props={props} arrowVars={arrowVars} type='arrowUrlLink' />
    ),
    arrowPathLink: (props) => (
      <ArrowLinks props={props} arrowVars={arrowVars} type='arrowPathLink' />
    ),
    arrowPlayerLink: (props) => (
      <ArrowLinks
        props={props}
        arrowVars={arrowVars}
        type='arrowPlayerLink'
        playerOpen={playerOpen}
        setPlayerOpen={setPlayerOpen}
      />
    ),
    arrowDonateLink: (props) => (
      <ArrowLinks
        props={props}
        arrowVars={arrowVars}
        type='arrowDonateLink'
        toggleDonate={toggleDonate}
        donateOpen={donateOpen}
      />
    ),
    buttonDonateLink: (props) => (
      <div>
        <button
          className='btn-black'
          props={props}
          arrowVars={arrowVars}
          type='buttonDonateLink'
          onClick={toggleDonate}
          donateOpen={donateOpen}>
          {props.text}
        </button>
        <DonateModal donateOpen={donateOpen} toggleDonate={toggleDonate} />
      </div>
    ),

    buttonLink: (props) => (
      <PageLink
        {...props.page}
        className={props.reversed ? 'btn-black' : 'btn-white'}
      />
    ),
    buttonAnchorLink: (props) => (
      <AnchorScroll
        href={`#${props?.anchor?.anchor?.current}`}
        className={props.reversed ? 'btn-black' : 'btn-white'}>
        {props?.anchor?.linkText}
      </AnchorScroll>
    ),
    buttonUrlLink: (props) => (
      <a
        href={props.url}
        target='_blank'
        rel='noreferrer'
        className={props.reversed ? 'btn-black' : 'btn-white'}>
        {props.text}
      </a>
    ),
    textColor: (props) => (
      <span {...props} style={{ color: props.color.value }} />
    ),
    eventReference: (props) => (
      <EventWidget setPopupOpen={setPopupOpen} {...props} />
    ),
    formReference: (props) => <FormWidget {...props} />,
    titleBlock: (props) => (
      <h1 className={`title-block ${props.noMargin && 'no-margin'}`}>
        {props.text}
      </h1>
    ),
    contactInfo: (props) => (
      <div className='prose-mt-0 prose-mb-0'>
        <a className='block' href={`mailto:${props.emailLink}`}>
          <h3>{props.emailLink}</h3>
        </a>
        <a className='block' href={`tel:${props.phoneLink}`}>
          <h3>{props.phoneLink}</h3>
        </a>
        <a
          className='block underline-important'
          href={props.visitLink}
          target='_blank'
          rel='noreferrer'>
          <h3>Visit us</h3>
        </a>
      </div>
    ),
    link: (props) => (
      <a
        href={props.url}
        target='_blank'
        rel='noreferrer'
        className='underline-important'>
        {props.children}
      </a>
    ),
    foundationDonations: (props) => <FoundationDonations {...props} />,
    highlight: (props) => (
      <span className='text-[#F04950]'>{props.children}</span>
    ),
    highlightYellow: (props) => (
      <span className='text-[#ffb615]'>{props.children}</span>
    ),
    highlightPurple: (props) => (
      <span className='text-[#b559a4]'>{props.children}</span>
    ),
  }; // eslint-disable-line

  return <PortableText content={text} serializers={serializers} {...props} />;
};
